import { useInvestmentAccountDetail, type InvestmentHolding } from '@egw/api'
import { Collapse, GenericError, Link, PageHeader } from '@egw/components'
import { WEALTH_ROUTES } from '@egw/constants'
import { useTranslation } from '@egw/i18n'
import { Currency, getCurrencySymbol, getFormattedBalanceForOverview } from '@egw/utils'
import { type FC } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useParams } from 'react-router'
import { buildHoldingsPageData } from '../../utils'

export const AccountHoldings: FC = () => {
  const { t } = useTranslation('features-holdings')

  const {
    alias = '',
    assetAllocation = '',
    category = '',
  } = useParams<{
    alias: string
    assetAllocation: string
    category: string
  }>()

  const {
    data: investmentAccountDetail,
    isLoading,
    isError,
  } = useInvestmentAccountDetail(parseInt(alias), {
    enabled: Boolean(alias),
  })

  const data = buildHoldingsPageData({
    data: investmentAccountDetail,
    assetAllocationParam: assetAllocation,
    categoryParam: category,
  })

  const pageTitle = data?.category ? t(`category_titles.${data.category}`) : t('holdings.category')

  const pageSubtitle = assetAllocation ? t(`holdings.subtitles.${assetAllocation}`) : ''

  const chipLabel = t('holdings.percentage_of_asset', { percentage: data?.percentage ?? 0, assetName: pageSubtitle })

  const formattedTotalBalance = data?.amount ? getFormattedBalanceForOverview(data.amount) : undefined

  return (
    <>
      {isError || data === undefined ? (
        <div className="egw-container">
          <GenericError errorMessage={t('holdings.overview_fatal_error')} />
        </div>
      ) : null}
      {isLoading ? (
        <div className="egw-container">
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        </div>
      ) : null}

      {!isLoading ? (
        <PageHeader
          title={pageTitle}
          subtitle={pageSubtitle}
          backLinkUrl={WEALTH_ROUTES.INVESTMENTS_ACCOUNT_HOLDINGS.backPath?.replace(':alias', alias.toString())}
        />
      ) : null}

      <div className="egw-container pt-0">
        {data ? (
          <div className="flex flex-col gap-6">
            <div>
              <span id="transaction_method" className="py-1 text-meta font-light w-fit">
                {chipLabel}
              </span>
              <Collapse
                title={t(`about.titles.${assetAllocation}.${category}`)}
                className="text-action text-sm font-semibold justify-start"
              >
                <div className="p-4 bg-alertInfoBg text-meta text-sm flex flex-col gap-4">
                  <p>{t(`about.descriptions.${assetAllocation}.${category}`)}</p>
                </div>
              </Collapse>
            </div>
            <span
              data-testid="overview-customer-networth"
              className="mb-1.5 font-noto font-light text-3xl md:text-4xl lg:text-5xl"
              id="total_balance"
            >
              <span
                data-testid="overview-networth-currency"
                className="text-meta text-3xl relative bottom-0 md:bottom-[.625rem]"
              >
                {getCurrencySymbol(String(data.amount.currencyCode))}
              </span>
              <span data-testid="overview-networth-whole" className="text-header left-12 text-right font-semibold">
                {formattedTotalBalance?.integers}
              </span>
              <span data-testid="overview-networth-decimal" className="text-meta font-light text-3xl">
                {formattedTotalBalance?.cents}
              </span>
            </span>
            <div>
              <div className="mt-8 flex flex-col items-start justify-start ">
                {
                  // map over categories
                  data.holdings.map(({ name, symbol, amount }: InvestmentHolding) => {
                    return (
                      <Link
                        href={WEALTH_ROUTES.INVESTMENTS_ACCOUNT_HOLDINGS_DETAILS.path
                          .replace(':alias', alias.toString())
                          .replace(':assetAllocation', assetAllocation)
                          .replace(':category', category)
                          .replace(':symbol', symbol)}
                        key={symbol}
                        className="flex w-full flex-row justify-between border-light last-of-type:border-b-0 border-b py-6"
                      >
                        <div className="flex flex-row gap-7">
                          {/* name */}
                          <div className="flex flex-col items-start">
                            <p className="text-body text-base">{name}</p>
                            <p className="text-meta text-sm">{symbol}</p>
                          </div>
                        </div>
                        {/* money */}
                        <div className="flex flex-row justify-between ">
                          <div className="flex flex-row gap-1">
                            <div className="text-meta text-base font-semibold">
                              {amount.currencyCode ? getCurrencySymbol(amount.currencyCode) : null}
                            </div>
                            <span className="text-header text-base font-semibold">
                              {Currency.integerCents.onlyMinus(amount.amount)}
                            </span>
                          </div>
                        </div>
                      </Link>
                    )
                  })
                }
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </>
  )
}
